// パッケージ間の接続仕様。battle（戦闘）・meta（育成）・worker（サーバー）・web（画面）がこの型で繋がる。
// 変更するときは Documentation/ARCHITECTURE.md も更新する。

export type CharId = 'akari' | 'kazamaru' | 'suzune' | 'yuzuha';
export const CHAR_IDS: readonly CharId[] = ['akari', 'kazamaru', 'suzune', 'yuzuha'];

/** 建物。櫓・竹柵・幟・落とし穴・修繕小屋・鳥居・狐火灯籠 */
export type BuildingType = 'yagura' | 'sakaku' | 'nobori' | 'otoshiana' | 'shuzen' | 'torii' | 'kitsunebi' | 'wara' | 'horoku' | 'ozutsu';
export const BUILDING_TYPES: readonly BuildingType[] = ['yagura', 'sakaku', 'nobori', 'otoshiana', 'shuzen', 'torii', 'kitsunebi', 'wara', 'horoku', 'ozutsu'];
/** 状態異常（悪い効果）。火傷＝手番の最初にダメージ／足止め＝移動-1／封印＝術が使えない／呪い＝回復が半分 */
export type BadStatusKey = 'burn' | 'bind' | 'seal' | 'curse';
export const BAD_STATUS_KEYS: readonly BadStatusKey[] = ['burn', 'bind', 'seal', 'curse'];
/** 良い効果（アイテム）。剛力＝与ダメ増／守り＝被ダメ減／清め＝状態異常にかからない */
export type BuffKey = 'might' | 'ward' | 'blessed';
export type StatusKey = BadStatusKey | BuffKey;
export const STATUS_KEYS: readonly StatusKey[] = ['burn', 'bind', 'seal', 'curse', 'might', 'ward', 'blessed'];

/** 戦闘アイテム。出撃前に2種類を選び、毎戦その数だけ支給される（使っても手番は終わらない・1手番に1回） */
export type ItemId = 'heal' | 'power' | 'guard' | 'swift' | 'purify' | 'spirit';
export const ITEM_IDS: readonly ItemId[] = ['heal', 'power', 'guard', 'swift', 'purify', 'spirit'];

export type Side = 'blue' | 'red'; // blue = プレイヤー（下）、red = 敵（上）

/** 育成（meta）が計算し、戦闘（battle）が受け取る出撃時の最終性能。戦闘側は育成の中身を知らない。 */
export interface BattleLoadout {
  charId: CharId;
  level: number; // 主Lv（表示用）
  stats: { hp: number; atk: number; def: number; agi: number; move: number; range: number };
  skill: {
    power: number; // 術の倍率に掛ける（1 = 基本）
    cdDelta: number; // 術の待ち手番の増減（-1 で1手番早い）
    splashBonus: number; // 範囲ダメージ割合への加算
  };
  build: {
    unlocked: BuildingType[]; // 建てられる建物
    levels: Partial<Record<BuildingType, number>>; // 建物図面のLv（0 から）
    maxCount: number; // 同時に建っていられる数
    perTurn: number; // 1手番に建てられる回数
    costMult: number; // 陣材費の倍率
    costFlat?: number; // 陣材費から引く数（最低1。古い setup には無い）
    hpMult: number;
    powerMult: number; // 櫓・落とし穴などの効果の倍率
    range: number; // 自分から何マス先まで建てられるか
  };
  materials: { start: number; incomeBonus: number };
  /** 回復薬の数と回復量（最大HPの割合）。items が無い古い setup は回復薬だけをこの数持つ */
  potions: { count: number; heal: number };
  /** 持ち出すアイテム（種類と数）。古い setup には無い */
  items?: { id: ItemId; count: number }[];
  diceMin: number; // 移動の歩数の最低（通常1。賽の出目は変えず、合計から決まる歩数をこの値まで引き上げる）
  /** 賽の目で効く効果（装備の銘・お守り。rules 2 から）。古い setup には無い */
  dice?: DiceMods;
  respawnDelta: number; // 復活までの手番の増減
  territoryBonus: number; // 陣地率への加算（0.05 = +5%）
  mult: { core: number; boss: number; neutral: number; taken: number }; // 与ダメ（本陣・ボス・中立）と被ダメの倍率
  lifesteal: number; // 与ダメの何割を回復するか
  flags: string[]; // 奥義・お守りの特殊効果のキー（戦闘側で解釈）
  /** 持っていく術（data/jutsu.ts の id）。無ければキャラの基本の術 */
  jutsu?: string;
  /** お供（表示用。能力そのものは数値と flags の comp:* に入っている）。古い setup には無い */
  companion?: { enemyId: string; star: number; level?: number; ability: string } | null;
  /** 式神の持っているだけの効果（上限後。表示と戦力の補正用。能力そのものは stats などに入っている）。古い setup には無い */
  shikigami?: Partial<Record<string, number>>;
}

/**
 * 賽の目で効く効果（賽3つの合計・役で決まる。その手番だけ）。
 * even/high/pair/role＝与ダメ+、odd＝次の手番まで被ダメ-、low＝移動+、roleMat＝役で陣材+、skill＝術の賽の上がり幅+
 */
export interface DiceMods {
  even?: number;
  odd?: number;
  high?: number;
  low?: number;
  pair?: number;
  role?: number;
  roleMat?: number;
  skill?: number;
  /** 賽の合計が大目に届かない手番の与ダメ（負＝下がる。博徒の大目狙い） */
  lowAtk?: number;
  /** 役が出なかった手番の与ダメ（負＝下がる。博徒の役者） */
  noRole?: number;
}
export const DICE_MOD_KEYS = ['even', 'odd', 'high', 'low', 'pair', 'role', 'roleMat', 'skill', 'lowAtk', 'noRole'] as const;
/** 賽の条件の効果の範囲（setup で丸める）：odd は負（被ダメが増える）も、lowAtk・noRole は負だけ */
export const DICE_MOD_RANGE: Record<(typeof DICE_MOD_KEYS)[number], [number, number]> = {
  even: [0, 1], odd: [-0.5, 1], high: [0, 1], low: [0, 3], pair: [0, 1], role: [0, 1.5], roleMat: [0, 3], skill: [0, 1], lowAtk: [-0.5, 0], noRole: [-0.5, 0],
};

export type TargetRef = { kind: 'actor'; id: number } | { kind: 'core'; side: Side };

/** プレイヤーの操作。サーバー検証ではこの列を再生する。 */
export type Command =
  | { t: 'roll' }
  | { t: 'move'; to: number; route?: number[] }
  /** ougi＝妖力の奥義（術の代わり。skill は無視） */
  | { t: 'attack'; target: TargetRef; skill?: boolean; ougi?: boolean }
  | { t: 'build'; type: BuildingType; cell: number }
  /** 自分の建物を壊す（どこからでも。手番は終わらない） */
  | { t: 'demolish'; cell: number }
  /** 自分を中心に放つ術（data/jutsu.ts の strike 以外）。cell＝縮地の着地点。ougi＝奥義版 */
  | { t: 'cast'; cell?: number; ougi?: boolean }
  | { t: 'potion' }
  /** アイテムを使う（手番は終わらない・1手番に1回） */
  | { t: 'item'; id: ItemId }
  | { t: 'wait' };

export interface BattleSetup {
  stageId: number; // 1..100
  difficulty: number; // 0 = 通常。1以上は上の難易度
  loadout: BattleLoadout;
  seed: number;
  /**
   * ルールの版。無い＝昔の戦い（途中の戦い・記録を昔のまま再現する）。1＝移動はできるだけ塗る回り道。2＝賽3つ（役・賽の条件の効果・術の賽）。
   * 3＝戦場の種類に色を付ける（battle/config.ts の v3）：敵の武将も新しいAI（ai.ts の AI_V3_RULES）・防衛戦の攻め手が強い・砦（章の9番目）は手番の上限・武将の傾向（本陣速攻・武将狙い）が強い。
   * 新しい戦いは RULES_VERSION
   */
  rules?: number;
}
/** 新しく始める戦いのルールの版（BattleSetup.rules） */
export const RULES_VERSION = 3;

/** 戦闘中の宝箱の中身（種類だけ。量は meta がステージから決める） */
export type ChestLoot = 'zeni' | 'reidama' | 'equip' | 'omamori';
export const CHEST_LOOTS: readonly ChestLoot[] = ['zeni', 'reidama', 'equip', 'omamori'];

export interface BattleResult {
  win: boolean;
  reason: 'core' | 'lost' | 'timeout' | 'invalid' | 'defense';
  playerTurns: number;
  ticks: number;
  heroKOs: number; // 自分のヒーローが倒された回数
  allRelaysOwned: boolean; // 決着時に龍脈をすべて自軍が持っていたか
  stars: [boolean, boolean, boolean]; // 勝利・戦闘不能なし・龍脈全占領
  kills: Record<string, number>; // 敵ID → 倒した数（式神・図鑑・ドロップ用）
  bossKilled: boolean;
  enemyBuildingsDestroyed: number; // 材木の報酬に使う
  buildingsBuilt: number;
  /** 自分が開けた宝箱の中身（開けた順。meta が報酬にする）。古い結果には無い */
  chests?: ChestLoot[];
  /** 決着時の妖力の段階（1..3）と累計の撃破ポイント。古い結果には無い */
  surge?: { blue: number; red: number };
  surgePoints?: { blue: number; red: number };
}

/** ステージの公開情報（画面・育成・サーバーが参照）。盤面の詳細は battle 内部。 */
export interface StageInfo {
  id: number; // 1..100
  chapter: number; // 1..10
  index: number; // 章内 1..10
  name: string;
  enemyIds: string[]; // 出てくる敵（図鑑・ドロップ・式神の対象）
  bossId: string | null;
  isChapterBoss: boolean; // 章の10番目
  terrain: string; // 背景・地形の種類
  enemyLevel: number;
  /** 特別な戦い（defense＝防衛戦） */
  mode?: 'defense';
  /** 敵の武将の傾向（battle/config.ts の rivalAi。無ければ通常） */
  rivalAi?: 'rush' | 'builder' | 'hunter' | 'farmer';
}
